import React, { useEffect, useMemo, useState } from "react";
import client from "../api/client";
import { HOUSE_DISPOSITION_OPTIONS } from "../constants";
import { getDispositionColor } from "../utils/dispositionColors";
import { ColorField, SectionCard } from "./ThemeColorFields";

const DEFAULT_OPTIONS = HOUSE_DISPOSITION_OPTIONS.map(({ value, label }) => ({ value, label }));

const DispositionColorsSection = ({ tenantId, colors, onChange, title }) => {
  const [options, setOptions] = useState(DEFAULT_OPTIONS);
  const [isLoading, setIsLoading] = useState(Boolean(tenantId));

  useEffect(() => {
    if (!tenantId) return undefined;

    let isMounted = true;
    setIsLoading(true);
    client
      .get(`/tenants/${tenantId}/disposition-options`)
      .then((res) => {
        if (isMounted && res.data?.options?.length) setOptions(res.data.options);
      })
      .catch(() => {
        if (isMounted) setOptions(DEFAULT_OPTIONS);
      })
      .finally(() => {
        if (isMounted) setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [tenantId]);

  const rows = useMemo(() => {
    const known = new Set(options.map((option) => option.value));
    const savedOnly = Object.keys(colors || {})
      .filter((value) => !known.has(value))
      .map((value) => ({ value, label: value }));
    return [...options, ...savedOnly];
  }, [options, colors]);

  const setColor = (value, color) => onChange({ ...(colors || {}), [value]: color });

  return (
    <SectionCard
      title={title}
      description="Color used for each household disposition in the Households table and on the map. New options added in HubSpot appear here automatically in gray until you pick a color."
    >
      {isLoading ? (
        <h6 className="text-canvas-directoryText font-normal">Loading dispositions...</h6>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-x-4 gap-y-4">
            {rows.map(({ value, label }, index) => (
              <div key={value} className="flex flex-col gap-1">
                <ColorField
                  label={label}
                  value={getDispositionColor(value, colors)}
                  onChange={(color) => setColor(value, color)}
                  align={index % 4 === 3 ? "right" : "left"}
                />
              </div>
            ))}
          </div>
          <div className="mt-4">
            <button
              type="button"
              className="font-outfit text-[12px] uppercase font-bold border border-canvas-primary text-canvas-primary py-2.5 px-7 rounded-md hover:bg-canvas-primary/5 transition-colors"
              onClick={() => onChange({})}
            >
              Reset Disposition Colors
            </button>
          </div>
        </>
      )}
    </SectionCard>
  );
};

export default DispositionColorsSection;
